import { useState, useCallback } from "react";
import { useLoading } from "./useLoading";

// Define the type structure
export type CommentType = {
  id: number;
  parent_id: number | null;
  post_id: number;
  author_id: number;
  author_name: string;
  author_photo_url: string;
  comment: string;
  likes: number;
  commented_at: string;
  total_replies: number;
  replies?: CommentType[];
};

type UseCommentAndReplyProps = {
  initialComments?: CommentType[];
  postId: number;
  onAddComment: (content: string, postId: number) => Promise<CommentType>;
  onAddReply: (
    parentId: number,
    content: string,
    postId: number
  ) => Promise<CommentType>;
  onLike: (commentId: number) => Promise<{ message: string }>;
  onLoadReplies: (commentId: number) => Promise<CommentType[]>;
};

export function useCommentAndReply({
  initialComments = [],
  postId,
  onAddComment,
  onAddReply,
  onLike,
  onLoadReplies,
}: UseCommentAndReplyProps) {
  const [comments, setComments] = useState<CommentType[]>(initialComments);
  const [expandedComments, setExpandedComments] = useState<Set<number>>(
    new Set()
  );
  const { startLoading, stopLoading } = useLoading();
  const [loadingStates, setLoadingStates] = useState<{
    [key: number]: boolean;
  }>({});
  const [newCommentContent, setNewCommentContent] = useState<string>("");

  const updateCommentRecursively = useCallback(
    (
      comments: CommentType[],
      commentId: number,
      updateFn: (comment: CommentType) => CommentType
    ): CommentType[] =>
      comments.map((comment) =>
        comment.id === commentId
          ? updateFn(comment)
          : {
              ...comment,
              replies: comment.replies
                ? updateCommentRecursively(comment.replies, commentId, updateFn)
                : undefined,
            }
      ),
    []
  );

  const handleAddComment = useCallback(async () => {
    if (!newCommentContent.trim()) return;
    startLoading();
    const newComment = await onAddComment(newCommentContent, postId);
    setComments((prev) => [...prev, newComment]);
    setNewCommentContent("");
    stopLoading();
  }, [newCommentContent, onAddComment, postId]);

  const handleAddReply = useCallback(
    async (parentId: number, content: string) => {
      startLoading();
      const newReply = await onAddReply(parentId, content, postId);
      setComments((prevComments) =>
        updateCommentRecursively(prevComments, parentId, (comment) => ({
          ...comment,
          total_replies: comment.total_replies + 1,
          replies: [...(comment.replies || []), newReply],
        }))
      );
      stopLoading();
    },
    [onAddReply, postId, updateCommentRecursively]
  );

  const handleLike = useCallback(
    async (commentId: number) => {
      try {
        startLoading();
        const result = await onLike(commentId); // Call the API to toggle the like state
        const { message } = result; // Extract the message

        setComments((prevComments) =>
          updateCommentRecursively(prevComments, commentId, (comment) => {
            if (message === "added") {
              return { ...comment, likes: comment.likes + 1 }; // Increment likes
            } else if (message === "removed") {
              return { ...comment, likes: Math.max(comment.likes - 1, 0) }; // Decrement likes but prevent negative values
            } else if (message === "error") {
              console.error(
                "Error updating like state for comment:",
                commentId
              );
            }
            return comment; // Return the original comment unchanged in case of error
          })
        );
        stopLoading();
      } catch (error) {
        stopLoading();
        console.error("Error toggling like state:", error);
      }
    },
    [onLike, updateCommentRecursively]
  );

  // const handleLoadReplies = useCallback(
  //   async (commentId: number) => {
  //     setLoadingStates((prev) => ({ ...prev, [commentId]: true }));
  //     await onLoadReplies(commentId);
  //     setExpandedComments((prev) => new Set(prev).add(commentId));
  //     setLoadingStates((prev) => ({ ...prev, [commentId]: false }));
  //   },
  //   [onLoadReplies]
  // );
  const handleLoadReplies = useCallback(
    async (commentId: number) => {
      try {
        setLoadingStates((prev) => ({ ...prev, [commentId]: true }));
        startLoading();
        // Fetch the replies from the API
        const fetchedReplies: CommentType[] = await onLoadReplies(commentId);
        // Update the comments state to include the fetched replies
        setComments((prevComments) =>
          updateCommentRecursively(prevComments, commentId, (comment) => ({
            ...comment,
            replies: fetchedReplies, // Add fetched replies to the comment
          }))
        );
        stopLoading();

        // Mark the comment as expanded
        setExpandedComments((prev) => new Set(prev).add(commentId));
      } catch (error) {
        console.error("Failed to load replies:", error);
        stopLoading();
      } finally {
        setLoadingStates((prev) => ({ ...prev, [commentId]: false }));
      }
    },
    [onLoadReplies, updateCommentRecursively]
  );

  return {
    comments,
    newCommentContent,
    setNewCommentContent,
    expandedComments,
    loadingStates,
    handleAddComment,
    handleAddReply,
    handleLike,
    handleLoadReplies,
  };
}
